/**
 * Shared work-item row UI and sorting helpers (Team profile Overview & My Tasks).
 */

import { issueStatusLabel, getWorkflowStatusOptions } from '../constants/issueStatuses';
import { DEFAULT_DEPARTMENT } from '../constants/departments';

export const PRIO_ORDER = { Blocker: 0, Critical: 1, High: 2, Medium: 3, Low: 4 };

export const TERMINAL_STATUSES = new Set([
  'Fixed', 'Not Reproducible', 'Invalid', "Won't Fix", 'Duplicate', 'Closed', 'Done',
]);

export const isTerminalStatus = (st) => TERMINAL_STATUSES.has(st);

export const statusReviewRank = (st) => {
  if (isTerminalStatus(st)) return 80;
  if (st === 'In Progress') return 1;
  if (st === 'Open') return 2;
  if (st === 'Testing') return 3;
  if (st === 'Blocked') return 4;
  return 50;
};

export const sortRowsForReview = (rows) =>
  [...rows].sort((a, b) => {
    const d = statusReviewRank(a.status) - statusReviewRank(b.status);
    if (d !== 0) return d;
    return (PRIO_ORDER[a.priority] ?? 99) - (PRIO_ORDER[b.priority] ?? 99);
  });

export const sortBugsForReview = (rows) =>
  [...rows].sort((a, b) => {
    const pr = (PRIO_ORDER[a.priority] ?? 99) - (PRIO_ORDER[b.priority] ?? 99);
    if (pr !== 0) return pr;
    return statusReviewRank(a.status) - statusReviewRank(b.status);
  });

/** Newest activity first (My Tasks bugs default). */
export const sortBugsByTimestamp = (rows) =>
  [...rows].sort((a, b) => {
    const ta = new Date(a.updated_at || a.createddate || 0).getTime();
    const tb = new Date(b.updated_at || b.createddate || 0).getTime();
    return tb - ta;
  });

export const isBugRow = (t) => (t?.issuetype || '').toLowerCase() === 'bug';
export const taskRowsOnly = (rows) => (rows || []).filter((t) => !isBugRow(t));
export const bugRowsOnly = (rows) => (rows || []).filter(isBugRow);

const isStoryRow = (row) => String(row?.issuetype || '').toLowerCase() === 'story';
const isTaskRow = (row) => String(row?.issuetype || '').toLowerCase() === 'task';

/**
 * Group My Tasks rows into story → child tasks hierarchy.
 * Tasks whose parent story is not in the list use parent_story_* fields for a virtual story header.
 */
export function buildMyTasksHierarchy(rows = []) {
  const sorted = sortRowsForReview(rows);
  const byId = new Map(sorted.map((r) => [String(r.issueid), r]));

  const stories = sorted.filter(isStoryRow);
  const tasks = sorted.filter(isTaskRow);
  const other = sorted.filter((r) => !isStoryRow(r) && !isTaskRow(r));

  const childrenByStoryId = new Map();
  const virtualStories = new Map();

  tasks.forEach((task) => {
    const parentId = task.parent_id ?? task.parent_story_issueid;
    if (parentId == null || parentId === '') return;

    const parentKey = String(parentId);
    const parent = byId.get(parentKey);
    const isStoryParent = isStoryRow(parent)
      || String(task.parent_issuetype || '').toLowerCase() === 'story';
    if (!isStoryParent) return;

    if (!parent) {
      if (!virtualStories.has(parentKey)) {
        virtualStories.set(parentKey, {
          issueid: parentId,
          issuetype: 'Story',
          description: task.parent_story_title || `Story #${parentId}`,
          projectname: task.projectname,
          sprint_name: task.sprint_name,
          priority: task.priority,
          status: 'Open',
          _virtual: true,
        });
      }
    }

    if (!childrenByStoryId.has(parentKey)) childrenByStoryId.set(parentKey, []);
    childrenByStoryId.get(parentKey).push(task);
  });

  const groupedTaskIds = new Set();
  childrenByStoryId.forEach((children) => {
    children.forEach((t) => groupedTaskIds.add(String(t.issueid)));
  });

  const orphanTasks = tasks.filter((t) => !groupedTaskIds.has(String(t.issueid)));

  childrenByStoryId.forEach((children, sid) => {
    childrenByStoryId.set(sid, sortRowsForReview(children));
  });

  const storyHeaders = [...stories];
  virtualStories.forEach((story, sid) => {
    if (!byId.has(sid)) storyHeaders.push(story);
  });

  const storyGroups = sortRowsForReview(storyHeaders).map((story) => ({
    story,
    children: childrenByStoryId.get(String(story.issueid)) || [],
  }));

  return { storyGroups, orphanTasks: sortRowsForReview(orphanTasks), other };
}

export const CLOSED_BUG_STATUSES = new Set([
  'Closed', 'Not Reproducible', 'Invalid', "Won't Fix", 'Duplicate', 'By Design', 'Cannot Reproduce',
]);

export const STATUS_COLORS = {
  Open: '#64748b',
  'In Progress': '#2C8FC7',
  Testing: '#8b5cf6',
  Blocked: '#ef4444',
  Done: '#22c55e',
  Fixed: '#22c55e',
  Closed: '#22c55e',
};

export const PRIORITY_META = {
  Blocker: { color: '#dc2626', bg: '#fef2f2' },
  Critical: { color: '#ea580c', bg: '#fff7ed' },
  High: { color: '#d97706', bg: '#fefce8' },
  Medium: { color: '#2C8FC7', bg: '#EAF6FD' },
  Low: { color: '#64748b', bg: '#f8fafc' },
};

export const parentContextLine = (task) => {
  const type = (task.issuetype || '').toLowerCase();
  if (type !== 'task') return null;
  const pid = task.parent_story_issueid ?? task.parent_id;
  const title = (task.parent_story_title || '').trim();
  const ptype = (task.parent_issuetype || '').toLowerCase();
  if (!pid && !title) return null;
  const label = ptype === 'epic' ? 'Epic' : ptype === 'story' ? 'Story' : 'Parent';
  return { label, title: title || `Issue #${pid}`, id: pid };
};

const stopCardNav = (e) => e.stopPropagation();

function WorkItemStatusControl({ task, allowStatusChange, onStatusChange, workflowOptions, department }) {
  const statusColor = STATUS_COLORS[task.status] || (isTerminalStatus(task.status) ? '#22c55e' : '#64748b');
  const badgeStyle = {
    fontSize: '0.68rem',
    fontWeight: 700,
    padding: '3px 8px',
    borderRadius: 6,
    background: `${statusColor}18`,
    color: statusColor,
  };

  if (!allowStatusChange || typeof onStatusChange !== 'function') {
    return (
      <span style={badgeStyle}>
        {issueStatusLabel(task.status || 'Open', department) || task.status}
      </span>
    );
  }

  if (isTerminalStatus(task.status) || task.status === 'Closed') {
    return (
      <span
        style={{ ...badgeStyle, display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}
        onClick={stopCardNav}
        onMouseDown={stopCardNav}
        onPointerDown={stopCardNav}
      >
        ✓ {issueStatusLabel(task.status, department) || task.status}
        <button
          type="button"
          onClick={() => onStatusChange(task.issueid, 'Reopened')}
          style={{
            background: 'none',
            border: 'none',
            color: '#d97706',
            fontSize: '0.65rem',
            cursor: 'pointer',
            fontWeight: 700,
            padding: 0,
          }}
        >
          ↩ Reopen
        </button>
      </span>
    );
  }

  return (
    <select
      value={task.status || 'Open'}
      onClick={stopCardNav}
      onMouseDown={stopCardNav}
      onPointerDown={stopCardNav}
      onChange={(e) => onStatusChange(task.issueid, e.target.value)}
      style={{
        fontSize: '0.68rem',
        fontWeight: 700,
        padding: '3px 8px',
        borderRadius: 6,
        border: '1px solid #e2e8f0',
        background: `${statusColor}18`,
        color: statusColor,
        cursor: 'pointer',
        maxWidth: 140,
      }}
    >
      {workflowOptions.map(({ value, label }) => (
        <option key={value} value={value}>{label}</option>
      ))}
      <option disabled style={{ color: '#d1d5db' }}>──────</option>
      <option value="Closed">{issueStatusLabel('Done', department)}</option>
    </select>
  );
}

export const WorkItemCard = ({
  task,
  onOpen,
  allowStatusChange = false,
  onStatusChange = null,
  workflowOptions = null,
  department = DEFAULT_DEPARTMENT,
  isChildTask = false,
}) => {
  const p = PRIORITY_META[task.priority] || PRIORITY_META.Low;
  const title = task.description?.split('\n')[0] || 'Item';
  const sprintLabel = task.sprint_name ? `${task.sprint_name}` : 'Backlog';
  const parentCtx = isChildTask ? null : parentContextLine(task);
  const statusOptions = workflowOptions || getWorkflowStatusOptions(department);
  const isStory = isStoryRow(task);

  const openDetails = () => onOpen?.(task.issueid);

  return (
    <div
      role="button"
      tabIndex={0}
      title="View details"
      onClick={openDetails}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openDetails();
        }
      }}
      style={{
        width: '100%',
        textAlign: 'left',
        padding: isChildTask ? '0.7rem 0.85rem' : '0.85rem 1rem',
        background: isChildTask ? '#faf5ff' : '#fff',
        border: isChildTask ? '1px solid #ddd6fe' : '1px solid #e2e8f0',
        borderRadius: 10,
        borderLeft: `4px solid ${isChildTask ? '#7c3aed' : p.color}`,
        boxShadow: '0 1px 2px rgba(15,23,42,0.06)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        transition: 'box-shadow 0.15s, border-color 0.15s',
        boxSizing: 'border-box',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 4px 14px rgba(15,23,42,0.1)';
        e.currentTarget.style.borderColor = '#cbd5e1';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = '0 1px 2px rgba(15,23,42,0.06)';
        e.currentTarget.style.borderColor = '#e2e8f0';
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
          {task.issuetype && (
            <span
              style={{
                fontSize: '0.62rem',
                fontWeight: 800,
                letterSpacing: '0.04em',
                color: isStory ? '#5b21b6' : '#475569',
                background: isStory ? '#ede9fe' : '#f1f5f9',
                padding: '3px 7px',
                borderRadius: 6,
              }}
            >
              {String(task.issuetype).toUpperCase()}
            </span>
          )}
          <span
            style={{
              fontSize: '0.68rem',
              fontWeight: 700,
              padding: '3px 8px',
              borderRadius: 6,
              background: p.bg,
              color: p.color,
            }}
          >
            {task.priority}
          </span>
          <WorkItemStatusControl
            task={task}
            allowStatusChange={allowStatusChange}
            onStatusChange={onStatusChange}
            workflowOptions={statusOptions}
            department={department}
          />
          <span style={{ fontSize: '0.65rem', fontWeight: 600, color: '#94a3b8' }}>#{task.issueid}</span>
        </div>
        {parentCtx && (
          <div
            style={{
              fontSize: '0.72rem',
              color: '#475569',
              marginBottom: 6,
              lineHeight: 1.35,
              padding: '6px 8px',
              background: '#f1f5f9',
              borderRadius: 6,
              border: '1px solid #e2e8f0',
            }}
          >
            <span style={{ fontWeight: 800, color: '#334155', letterSpacing: '0.02em' }}>{parentCtx.label}</span>
            <span style={{ color: '#94a3b8', margin: '0 6px' }}>→</span>
            <span style={{ fontWeight: 600 }}>{parentCtx.title}</span>
            {parentCtx.id != null && (
              <span style={{ fontWeight: 600, color: '#94a3b8', marginLeft: 6 }}>#{parentCtx.id}</span>
            )}
          </div>
        )}
        <div style={{ fontSize: '0.92rem', fontWeight: 650, color: '#0f172a', lineHeight: 1.35 }}>{title}</div>
        <div
          style={{
            fontSize: '0.76rem',
            color: '#64748b',
            marginTop: 6,
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.35rem 0.75rem',
          }}
        >
          {task.projectname && <span>{task.projectname}</span>}
          <span style={{ color: '#cbd5e1' }}>·</span>
          <span>{sprintLabel}</span>
        </div>
      </div>
      <span style={{ fontSize: '1.1rem', color: '#94a3b8', fontWeight: 300, lineHeight: 1 }} aria-hidden>
        ›
      </span>
    </div>
  );
};

/**
 * Compact checklist row for a task inside a My Tasks story group.
 * Same visual language as the Story modal's task checklist: a checkbox that
 * toggles done/reopen directly (no need to open the task first), a title
 * that wraps instead of clamping, and a status pill on the right.
 */
export const StoryChecklistTaskRow = ({
  task,
  onOpen,
  onStatusChange,
  isToggling = false,
}) => {
  const isDone = isTerminalStatus(task.status);
  const title = task.description?.split('\n')[0] || 'Task';
  const assignee = task.assignee_name || task.assignee_email || '';

  const stop = (e) => e.stopPropagation();

  const handleToggle = (e) => {
    stop(e);
    if (isToggling || typeof onStatusChange !== 'function') return;
    onStatusChange(task.issueid, isDone ? 'Reopened' : 'Closed');
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen?.(task.issueid)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen?.(task.issueid);
        }
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '7px 10px',
        borderRadius: 8,
        border: '1px solid',
        borderColor: isDone ? '#bbf7d0' : '#e2e8f0',
        background: isDone ? '#f6fdf9' : '#fff',
        cursor: 'pointer',
        transition: 'background 0.15s, border-color 0.15s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = isDone ? '#effbf4' : '#f8fafc';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = isDone ? '#f6fdf9' : '#fff';
      }}
    >
      <button
        type="button"
        role="checkbox"
        aria-checked={isDone}
        aria-label={isDone ? 'Mark task as not done' : 'Mark task as done'}
        disabled={isToggling}
        onClick={handleToggle}
        onMouseDown={stop}
        onPointerDown={stop}
        style={{
          width: 20,
          height: 20,
          borderRadius: 5,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.75rem',
          fontWeight: 800,
          border: isDone ? '2px solid #16a34a' : '2px solid #cbd5e1',
          background: isDone ? '#16a34a' : '#fff',
          color: '#fff',
          cursor: isToggling ? 'wait' : 'pointer',
          opacity: isToggling ? 0.5 : 1,
          padding: 0,
          lineHeight: 1,
        }}
      >
        {isDone ? '✓' : ''}
      </button>
      <div
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: '0.85rem',
          fontWeight: 600,
          color: isDone ? '#94a3b8' : '#0f172a',
          lineHeight: 1.4,
          overflowWrap: 'anywhere',
          textDecoration: isDone ? 'line-through' : 'none',
          textDecorationColor: '#cbd5e1',
        }}
        title={title}
      >
        <span style={{ fontSize: '0.7rem', fontWeight: 700, color: isDone ? '#86efac' : '#a78bfa', marginRight: 6 }}>
          #{task.issueid}
        </span>
        {title}
        {assignee && (
          <span style={{ marginLeft: 8, fontSize: '0.72rem', fontWeight: 600, color: isDone ? '#a3aebd' : '#64748b' }}>
            {assignee}
          </span>
        )}
      </div>
      <span
        style={{
          flexShrink: 0,
          fontSize: '0.66rem',
          fontWeight: 700,
          padding: '3px 8px',
          borderRadius: 999,
          background: isDone ? '#dcfce7' : `${STATUS_COLORS[task.status] || '#64748b'}18`,
          color: isDone ? '#15803d' : (STATUS_COLORS[task.status] || '#64748b'),
          whiteSpace: 'nowrap',
        }}
      >
        {isDone ? '✓ Done' : (task.status || 'Open')}
      </span>
    </div>
  );
};

/**
 * Bold header row for a story group in My Tasks — checkbox marks every child
 * task done/reopened in one action, title, and a "N/M done" progress count.
 */
export const StoryGroupHeaderRow = ({
  story,
  doneCount,
  totalCount,
  onOpen,
  onMarkAllDone,
  isBusy = false,
}) => {
  const title = story.description?.split('\n')[0] || `Story #${story.issueid}`;
  const allDone = totalCount > 0 && doneCount === totalCount;
  const hasOpenChildren = totalCount > 0 && doneCount < totalCount;

  const stop = (e) => e.stopPropagation();

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen?.(story.issueid)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen?.(story.issueid);
        }
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px',
        borderRadius: 10,
        border: '1px solid #ddd6fe',
        borderLeft: '4px solid #7c3aed',
        background: '#faf5ff',
        cursor: 'pointer',
      }}
    >
      {totalCount > 0 ? (
        <button
          type="button"
          role="checkbox"
          aria-checked={allDone}
          aria-label={allDone ? 'Reopen all tasks in this story' : 'Mark all tasks in this story as done'}
          disabled={isBusy || (!hasOpenChildren && !allDone)}
          onClick={(e) => {
            stop(e);
            if (isBusy || typeof onMarkAllDone !== 'function') return;
            onMarkAllDone();
          }}
          onMouseDown={stop}
          onPointerDown={stop}
          title={allDone ? 'All tasks done' : 'Mark all tasks done'}
          style={{
            width: 22,
            height: 22,
            borderRadius: 6,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.8rem',
            fontWeight: 800,
            border: allDone ? '2px solid #16a34a' : '2px solid #a78bfa',
            background: allDone ? '#16a34a' : '#fff',
            color: '#fff',
            cursor: isBusy ? 'wait' : hasOpenChildren ? 'pointer' : 'default',
            opacity: isBusy ? 0.5 : 1,
            padding: 0,
          }}
        >
          {allDone ? '✓' : ''}
        </button>
      ) : (
        <span style={{ fontSize: '1rem', flexShrink: 0 }} aria-hidden>📖</span>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: '0.68rem',
            fontWeight: 800,
            letterSpacing: '0.04em',
            color: '#5b21b6',
          }}
        >
          STORY · #{story.issueid}
        </div>
        <div
          style={{
            fontSize: '0.92rem',
            fontWeight: 700,
            color: '#1e1b4b',
            lineHeight: 1.35,
            overflowWrap: 'anywhere',
          }}
        >
          {title}
        </div>
      </div>
      {totalCount > 0 && (
        <span
          style={{
            flexShrink: 0,
            fontSize: '0.72rem',
            fontWeight: 700,
            padding: '4px 10px',
            borderRadius: 999,
            background: allDone ? '#dcfce7' : '#ede9fe',
            color: allDone ? '#15803d' : '#5b21b6',
            whiteSpace: 'nowrap',
          }}
        >
          {allDone ? '✓ All done' : `${doneCount}/${totalCount} done`}
        </span>
      )}
    </div>
  );
};

/** Summary stat chip (profile Overview & My Tasks). */
export const StatBox = ({ label, value, color }) => (
  <div
    style={{
      padding: '1rem',
      background: '#fff',
      borderRadius: 8,
      border: '1px solid #e2e8f0',
      textAlign: 'center',
    }}
  >
    <div
      style={{
        fontSize: '1.75rem',
        fontWeight: 700,
        color,
        marginBottom: '0.25rem',
      }}
    >
      {value}
    </div>
    <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{label}</div>
  </div>
);
